import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared } from '@krp/client-runtime'

import type { CommandFeedback } from './feedback'
import { dropResource } from './keys'

/**
 * Итоги команд окон пакета по ключу `<rid>|<окно>`. Срок — `screen`: после F5
 * правду говорит сервер (перечитанные кассы и статус операции), а не память
 * о прошлом нажатии.
 */
export interface FeedbackState {
  byKey: Record<string, CommandFeedback>
}

const initialState: FeedbackState = { byKey: {} }

const feedbackSlice = createSlice({
  name: 'aqsi/feedback',
  initialState,
  reducers: {
    feedbackSet(state, action: PayloadAction<{ key: string; feedback: CommandFeedback }>) {
      state.byKey[action.payload.key] = action.payload.feedback
    },
    /** Завершён HTTP-запрос, а не кассовая операция; поздний ответ не стирает чужую ошибку. */
    feedbackSendingFinished(state, action: PayloadAction<{ key: string; command: string; target: string }>) {
      const current = state.byKey[action.payload.key]
      if (current?.phase === 'sending' && current.command === action.payload.command && current.target === action.payload.target)
        delete state.byKey[action.payload.key]
    },
    feedbackCleared(state, action: PayloadAction<{ key: string }>) {
      delete state.byKey[action.payload.key]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => dropResource(state.byKey, action.payload.resourceId))
  },
})

export const { feedbackSet, feedbackCleared, feedbackSendingFinished } = feedbackSlice.actions
export const feedbackReducer = feedbackSlice.reducer

/** Окна пакета, у которых есть итог команды. */
export const FEEDBACK_SCOPES = {
  rmk: 'rmk',
  register: (registerKey: string) => `register:${registerKey.toLowerCase()}`,
  catalog: 'catalog',
  // UC1c: продажа на месте и ручная сверка операции.
  sale: (registerKey: string) => `sale:${registerKey.toLowerCase()}`,
  resolve: (operationId: string) => `resolve:${operationId.toLowerCase()}`,
  // UC1d: окно возврата по чеку (номер чека кабинета — как в адресе окна).
  refund: (receiptId: string) => `refund:${receiptId}`,
} as const
